import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const NorthCarolinaRequirementsPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                State Compliance
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                North Carolina <span className="text-indigo-600">Cybersecurity Requirements</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Understanding North Carolina's Identity Theft Protection Act and its requirements for businesses handling personal information of NC residents.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-yellow-50 border-y border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 text-sm">
                    The information provided on this page is for general informational purposes only. State cybersecurity regulations are complex and subject to change. We recommend reaching out to our team to confirm you have covered all applicable requirements for North Carolina operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Overview */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                  <p className="text-gray-700 text-lg">
                    North Carolina's <strong>Identity Theft Protection Act</strong> (mainly Article 2A of Chapter 75 of the General Statutes) sets requirements for how businesses and government entities protect personal information of North Carolina residents and how they respond to security breaches.
                  </p>
                </div>

                {/* Who is Covered */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Who is Covered?</h2>
                  <div className="bg-indigo-50 rounded-lg p-6 border-l-4 border-indigo-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-indigo-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Any business or state/local government agency that owns or licenses "personal information" about a North Carolina resident</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-indigo-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>This can include financial institutions, lenders, servicers, and other organizations handling sensitive consumer data</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Key Expectations */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Cybersecurity and Data Handling Expectations</h2>
                  <p className="text-gray-700 text-lg mb-6">
                    While North Carolina's law is often discussed in the context of breach notification, it also embeds data security and identity-theft prevention concepts, including:
                  </p>
                  
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-shield-check-line text-indigo-600"></i>
                        Reasonable Security Measures
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                          <span>Businesses must take reasonable steps to protect personal information from unauthorized access, acquisition, or use that could result in harm or identity theft</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-user-forbid-line text-indigo-600"></i>
                        Restrictions on Social Security Numbers
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                          <span>SSNs may not be used as public identifiers, printed on mailed materials (with limited exceptions), or transmitted over the internet in unencrypted form</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                          <span>SSNs cannot be used as the sole means of authentication without additional factors</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-delete-bin-line text-indigo-600"></i>
                        Destruction of Records
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-indigo-600 flex-shrink-0 mt-1"></i>
                          <span>When disposing of records containing personal information, businesses must take reasonable steps to destroy the information (e.g., shredding, erasing, or otherwise making it unreadable)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Breach Notification */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Breach Notification Obligations</h2>
                  <p className="text-gray-700 text-lg mb-4">
                    A "security breach" generally means unauthorized access to or acquisition of unencrypted or un-redacted personal information that creates a risk of identity theft or other harm.
                  </p>
                  <p className="text-gray-700 text-lg mb-6">If a breach occurs, covered entities must:</p>
                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Conduct a prompt investigation</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Notify affected North Carolina residents "without unreasonable delay" once they determine misuse is likely, consistent with law enforcement or remedial needs</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Notify consumer reporting agencies if a large number of residents are affected</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>For certain entities, notify the NC Attorney General / Department of Justice</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Notices must include specific content such as the type of incident, kinds of information involved, and steps consumers can take</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Need Help with North Carolina Compliance?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our team helps businesses understand and implement the controls needed to meet North Carolina's Identity Theft Protection Act requirements.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/schedule-call" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-md whitespace-nowrap cursor-pointer">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Consultation
                  </button>
                </a>
                <a href="/risk-assessment" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors border-2 border-indigo-600 whitespace-nowrap cursor-pointer">
                    <i className="ri-shield-check-line mr-2"></i>
                    Get Compliance Assessment
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NorthCarolinaRequirementsPage;
